// types.ts
export interface ConversationContext {
  currentBuild?: string;
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  previousTopics: string[];
  followUpCount: number;
  lastQueryTimestamp: number;
  playstyle?: string[];
  timeInvestment?: 'casual' | 'regular' | 'hardcore';
  gameProgress?: 'early' | 'mid' | 'endgame';
  weaponPreferences?: EnhancedWeaponPreference[];
  masteryProgress?: MasteryProgress[];
  skillPatterns?: SkillGemPattern[];
  uniqueWeapons?: {
    name: string;
    enhancement: number;
    effects: string[];
  }[];
  spiritCharges?: number;
  armorBreakStatus?: 'none' | 'broken' | 'fully-broken';
}

interface WeaponPreference {
  type: keyof typeof POE2_WEAPONS;
  subtype?: string;
  mastery?: string[];
  enhancement?: number;
}

interface EnhancedWeaponPreference extends WeaponPreference {
  combination?: keyof typeof POE2_WEAPON_COMBINATIONS;
  unique?: string;
  enhancementPath?: string[];
  socketPreference?: {
    colors: string[];
    links: number;
  };
}

interface MasteryProgress {
  type: string;
  level: number;
  nodes: string[];
  thresholds: string[];
}

interface SkillGemPattern {
  type: 'normal' | 'meta' | 'spirit';
  colors: string[];
  supports: string[];
  synergies: string[];
  requirements: {
    level: number;
    attributes?: {
      strength?: number;
      dexterity?: number;
      intelligence?: number;
    };
    spiritCharges?: number;
  };
}

interface QueryIntent {
  category: 'build' | 'mechanics' | 'crafting' | 'general';
  keywords: string[];
  complexity: number;
}

// constants.ts
const POE2_WEAPONS = {
  SWORD: {
    types: ['one-handed sword', 'two-handed sword'],
    masteries: ['sword mastery'],
    classes: ['warrior', 'mercenary']
  },
  AXE: {
    types: ['one-handed axe', 'two-handed axe'],
    masteries: ['axe mastery'],
    classes: ['warrior']
  },
  SPEAR: {
    types: ['spear'],
    masteries: ['spear mastery'],
    classes: ['warrior', 'mercenary']
  },
  FLAIL: {
    types: ['flail'],
    masteries: ['flail mastery'],
    classes: ['warrior', 'monk']
  },
  BOW: {
    types: ['bow'],
    masteries: ['bow mastery'],
    classes: ['ranger']
  },
  CROSSBOW: {
    types: ['crossbow', 'heavy crossbow', 'repeating crossbow'],
    masteries: ['crossbow mastery'],
    classes: ['ranger']
  },
  QUARTERSTAFF: {
    types: ['quarterstaff'],
    masteries: ['staff mastery'],
    classes: ['monk']
  },
  STAFF: {
    types: ['staff'],
    masteries: ['spell staff mastery'],
    classes: ['witch', 'sorceress']
  },
  WAND: {
    types: ['wand'],
    masteries: ['wand mastery'],
    classes: ['witch', 'sorceress']
  }
};

const POE2_CLASSES = {
  WARRIOR: {
    ascendancies: ['titan', 'warbringer'],
    weaponPreferences: ['sword', 'axe', 'spear'],
    specialMechanics: ['break armor', 'heavy stun']
  },
  RANGER: {
    ascendancies: ['deadeye', 'pathfinder'],
    weaponPreferences: ['bow', 'crossbow'],
    specialMechanics: ['focus', 'precision']
  },
  WITCH: {
    ascendancies: ['infernalist', 'blood mage'],
    weaponPreferences: ['staff', 'wand'],
    specialMechanics: ['spirit charge', 'affliction']
  },
  SORCERESS: {
    ascendancies: ['stormweaver', 'chronomancer'],
    weaponPreferences: ['staff', 'wand'],
    specialMechanics: ['energy', 'elemental']
  },
  MERCENARY: {
    ascendancies: ['witchhunter', 'gemling legionnaire'],
    weaponPreferences: ['sword', 'spear'],
    specialMechanics: ['adaptable', 'tactical']
  },
  MONK: {
    ascendancies: ['invoker', 'acolyte of chayula'],
    weaponPreferences: ['quarterstaff', 'flail'],
    specialMechanics: ['valour', 'meditation']
  }
};

const POE2_WEAPON_COMBINATIONS = {
  DUAL_WIELD: {
    valid: [
      ['one-handed sword', 'one-handed sword'],
      ['one-handed sword', 'one-handed axe'],
      ['one-handed axe', 'one-handed axe'],
      ['dagger', 'dagger'],
      ['wand', 'wand']
    ],
    classes: ['warrior', 'duelist', 'hunter'],
    masteryRequired: 'dual wielding'
  },
  TWO_HANDED: {
    weapons: ['greatsword', 'battle axe', 'quarterstaff', 'bow', 'crossbow'],
    classes: ['warrior', 'barbarian', 'hunter'],
    restrictions: 'no off-hand'
  },
  WEAPON_SHIELD: {
    weapons: ['one-handed sword', 'one-handed axe', 'wand'],
    classes: ['warrior', 'paladin', 'templar'],
    masteryRequired: 'shield combat'
  }
};

const POE2_SKILL_SYSTEM = {
  GEM_TYPES: {
    META: {
      categories: ['enhancement', 'conversion', 'amplification'],
      requirements: ['meta socket', 'spirit charge']
    },
    SPIRIT: {
      categories: ['manifestation', 'embodiment', 'transcendence'],
      requirements: ['spirit charge', 'focus']
    },
    SKILL: {
      categories: ['attack', 'spell', 'utility'],
      requirements: ['appropriate socket']
    },
    SUPPORT: {
      categories: ['amplify', 'modify', 'enhance'],
      requirements: ['linked socket']
    }
  },
  MECHANICS: {
    affliction: {
      types: ['physical', 'elemental', 'void', 'divine'],
      stacks: true,
      duration: 'variable'
    },
    armorBreak: {
      stages: ['normal', 'broken', 'fully-broken'],
      effects: {
        'broken': 'increased damage taken',
        'fully-broken': 'critical weakness'
      }
    },
    spiritCharge: {
      max: 5,
      generation: ['kill', 'time', 'special'],
      usage: ['meta skills', 'spirit gems']
    }
  }
};

const POE2_SKILL_PATTERNS = {
  COLOR_COMBINATIONS: {
    "red-red": ["melee damage", "physical damage"],
    "red-blue": ["elemental attack", "conversion"],
    "blue-blue": ["spell power", "elemental damage"],
    "green-green": ["precision", "speed"],
    "red-green": ["physical projectile", "accuracy"],
    "blue-green": ["elemental projectile", "critical"]
  },
  SUPPORT_SYNERGIES: {
    ATTACK: {
      primary: ["melee physical", "attack speed", "brutality"],
      secondary: ["critical strikes", "elemental damage", "life leech"]
    },
    SPELL: {
      primary: ["spell damage", "cast speed", "elemental focus"],
      secondary: ["critical strikes", "penetration", "area effect"]
    }
  }
};

const POE2_MASTERY_SYSTEM = {
  WEAPON_MASTERY: {
    levels: [1, 3, 5, 7, 10],
    thresholds: {
      1: "basic proficiency",
      3: "advanced techniques",
      5: "master techniques",
      7: "expert mastery",
      10: "legendary mastery"
    }
  },
  SKILL_MASTERY: {
    types: ["physical", "elemental", "defensive", "mobility"],
    progression: {
      physical: ["increased damage", "bleed chance", "stun chance"],
      elemental: ["penetration", "exposure", "proliferation"],
      defensive: ["block chance", "damage reduction", "recovery"],
      mobility: ["movement speed", "dash charges", "phase through"]
    }
  }
};

export const POE2_UNIQUE_WEAPONS = {
  EARLY_GAME: {
    "Stormbreaker": {
      type: "battle axe",
      chapter: 1,
      effects: ["lightning damage", "shock chance"],
      enhancement: {
        max: 3,
        effects: ["increased shock duration", "chain lightning"]
      }
    },
    "Shadowweave": {
      type: "dagger",
      chapter: 2,
      effects: ["poison damage", "stealth bonus"],
      enhancement: {
        max: 3,
        effects: ["increased poison duration", "shadow step"]
      }
    }
  },
  MID_GAME: {
    "Soul Harvester": {
      type: "scythe",
      chapter: 4,
      effects: ["life steal", "soul charge"],
      enhancement: {
        max: 5,
        effects: ["increased soul charge", "spectral form"]
      }
    }
  },
  LATE_GAME: {
    "Dragon's End": {
      type: "greatsword",
      chapter: 7,
      effects: ["fire damage", "dragon aspect"],
      enhancement: {
        max: 7,
        effects: ["increased fire damage", "dragon flight"]
      }
    }
  }
};

export const SYSTEM_PROMPTS: {
  BASE: string;
  INITIAL_RESPONSE: string;
  DETAILED_RESPONSE: string;
  MECHANICS_DETAIL: string;
  SKILL_MECHANICS: string;
  SKILL_TREE_PLANNER: string;
} = {
  BASE: `You are a Path of Exile 2 (Beta 0.2.0) build advisor and mechanics expert.

When responding to queries:
1. First analyze the user's context:
   - POE2 experience level (ignore POE1 experience)
   - Time investment availability
   - Preferred weapon type and combat style
   - Current campaign progress
   - Specific goals in POE2 Beta

2. Consider POE2-specific mechanics:
   - Armor Break system
   - Spirit Charge mechanics
   - Meta and Spirit gem usage
   - Campaign boss mechanics
   - Trial and Waystone progression

3. Provide recommendations based on:
   - Current POE2 Beta meta
   - Available content in 0.2.0
   - Known boss strategies
   - Class-specific mechanics
   - Weapon mastery system

4. Analyze query intent:
   - Category: build, mechanics, crafting, or general
   - Complexity level assessment
   - Key topic identification

5. Consider skill system mechanics:
   - Gem Categories: ${Object.keys(POE2_SKILL_SYSTEM.GEM_TYPES).join(', ')}
   - Core Mechanics: ${Object.keys(POE2_SKILL_SYSTEM.MECHANICS).join(', ')}
   - Requirements and Synergies`,

  INITIAL_RESPONSE: `
IMPORTANT: Keep initial response under 900 characters.

Format your brief response as follows:

# Quick Overview
Core POE2 recommendation based on weapon types and skill gems (under 450 characters)

# Key Point
Most important POE2-specific mechanic to consider (under 400 characters)

# Follow-up
Ask ONE specific question about:
- Weapon and mastery preferences
- Meta/Spirit gem understanding
- Current chapter progress
- Boss fight experience
- Armor Break mechanics knowledge`,

  DETAILED_RESPONSE: `Provide POE2-specific information formatted as follows:

# Detailed Analysis
## Build Overview
Core weapon types, skill gems, and playstyle overview

## Progression Path
- Early chapters (1-3)
- Mid chapters (7-8)
- Late chapters (9-10)

## Technical Details
- Armor Break strategy
- Spirit Charge management
- Meta/Spirit gem usage
- Affliction handling

## Boss Strategies
Known mechanics and approaches

## Next Steps
Concrete progression goals

## Skill Integration
- Gem Type Selection: ${POE2_SKILL_SYSTEM.GEM_TYPES.SKILL.categories.join(', ')}
- Support Requirements
- Meta/Spirit Progression
- Mechanical Synergies

## System Mechanics
- Affliction Management
- Break Point Optimization
- Spirit Charge Usage
- Socket Requirements`,

  MECHANICS_DETAIL: `
When discussing game mechanics, consider:

1. Weapon Systems:
- Mastery thresholds: ${JSON.stringify(POE2_MASTERY_SYSTEM.WEAPON_MASTERY.thresholds)}
- Class restrictions
- Combination requirements

2. Skill Interactions:
- Color synergies: ${JSON.stringify(POE2_SKILL_PATTERNS.COLOR_COMBINATIONS)}
- Support gem optimization
- Spirit/Meta requirements

3. Class Mechanics:
${Object.entries(POE2_CLASSES).map(([className, details]) => 
  `- ${className}: ${details.specialMechanics.join(', ')}`
).join('\n')}

4. Progressive Systems:
- Spirit Charge management
- Armor Break mechanics
- Affliction handling
- Mastery progression`,

  SKILL_MECHANICS: `
When discussing skill mechanics, consider:

1. Gem Type Requirements:
${Object.entries(POE2_SKILL_SYSTEM.GEM_TYPES).map(([type, info]) => 
  `- ${type}: ${info.requirements.join(', ')}`
).join('\n')}

2. Core Mechanics:
- Affliction: ${POE2_SKILL_SYSTEM.MECHANICS.affliction.types.join(', ')}
- Armor Break: ${POE2_SKILL_SYSTEM.MECHANICS.armorBreak.stages.join(', ')}
- Spirit Charge: Max ${POE2_SKILL_SYSTEM.MECHANICS.spiritCharge.max}, Generated via ${POE2_SKILL_SYSTEM.MECHANICS.spiritCharge.generation.join(', ')}

3. Progression Considerations:
- Meta Requirements: ${POE2_SKILL_SYSTEM.GEM_TYPES.META.categories.join(', ')}
- Spirit Requirements: ${POE2_SKILL_SYSTEM.GEM_TYPES.SPIRIT.categories.join(', ')}
- Support Options: ${POE2_SKILL_SYSTEM.GEM_TYPES.SUPPORT.categories.join(', ')}`,

  SKILL_TREE_PLANNER: `
When advising on skill tree planning, consider:

1. Core Mechanics Understanding:
- Culling Strike: Kills enemies at health thresholds (Normal 30%, Magic 20%, Rare 10%, Unique 5%)
- Decimating Strike: Removes 5-30% life from full health enemies
- Heavy Stun: Physical and Melee damage each provide 50% more stun buildup
- Ancestral Boost: 20% more damage and area of effect
- Flames of Chayula: Different effects based on flame color (Red: Life leech, Blue: Mana leech, Purple: Chaos damage)

2. Build Planning Priorities:
- Character Level and Progression Stage
- Primary Attribute Focus (Strength, Dexterity, Intelligence)
- Core Skill Requirements
- Defensive Layer Requirements

3. Notable Node Selection:
- Key Defensive Nodes
- Primary Damage Scaling
- Utility and Quality of Life
- Mastery Point Investment

4. Path Optimization:
- Efficient Point Usage
- Travel Node Minimization
- Attribute Requirement Management
- Future Growth Potential

5. Build-Specific Recommendations:
- Class-Specific Nodes
- Weapon Type Synergies
- Skill Gem Requirements
- Mastery System Integration

6. Progression Guidelines:
- Early Game Priorities
- Mid-Game Transitions
- Late Game Optimization
- Respec Considerations

7. Try Your Build:
After reviewing these recommendations, you can test your build in our Skill Tree Planner:
- Visit https://poe2skills-ai.com/poe2skilltreeplanner to visualize the passive tree
- Experiment with different node combinations
- Save and share your builds
- Calculate total attribute requirements
- Plan your progression path

Ready to try it out? Head over to https://poe2skills-ai.com/poe2skilltreeplanner to experiment with your build!

Please provide your current:
- Class and Level
- Main Skill Choice
- Preferred Playstyle
- Current Campaign Progress

This will help me provide targeted skill tree recommendations.`,
};

// DeepSeekClient.ts
export class DeepSeekClient {
  async getResponse(query: string, sessionId: string = 'default'): Promise<string> {
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          sessionId,
        })
      });

      if (!response.ok) {
        throw new Error('AI proxy failed');
      }

      const data = await response.json() as { content?: string; error?: string };
      return data.content || data.error || 'Sorry, the AI service returned an empty response.';

    } catch (error) {
      console.error('AI proxy error:', error);
      return 'Sorry, both AI services are currently unavailable. Please try again later.';
    }
  }
}

// Usage example
/*
const advisor = new DeepSeekClient();

async function example() {
  const sessionId = 'user123';
  
  // Initial query
  let response = await advisor.getResponse("I'm new to POE2, what's a good starting weapon type?", sessionId);
  console.log(response);

  // Follow-up query
  response = await advisor.getResponse("Tell me more about sword mastery progression", sessionId);
  console.log(response);
}
*/
